import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { usePortalActiveClinic } from '@/lib/portalActiveClinic';
import { logoUrlFromBranding, resolveClinicLogoUrl } from '@/lib/clinicBranding';
import type { Database, Json } from '@/types/database.generated';
import {
  isPatientCancellationAllowed,
  isPatientReschedulingAllowed,
  isPostgrestMissingColumnError,
  resolvePatientPortalClinicPolicy,
  resolvePaymentTiming,
  type PaymentTiming,
} from '@/lib/bookingPolicies';
import {
  resolvePatientFacingAppointmentStatus,
  type PatientFacingAppointmentStatus,
} from '@/utils/appointmentEditability';
import {
  isPreferredInvoicePaymentMethod,
  resolvePortalPaymentKind,
  type PortalPaymentKind,
} from '@/lib/portalAppointmentPayment';
import { practitionerDisplayName } from '@/lib/courseStepSlotPicker';

export type AppointmentStatus = Database['public']['Enums']['appointment_status'];
export type PaymentStatus = Database['public']['Enums']['payment_status'];
export type VisitStatus = Database['public']['Enums']['visit_status'];
export type { PortalPaymentKind };
export { isPortalAppointmentPaid, portalPaymentStatusKey, resolvePortalPaymentKind } from '@/lib/portalAppointmentPayment';

export interface PortalAppointmentItem {
  id: string;
  clinic_id: string;
  clinic_name: string;
  clinic_slug: string | null;
  clinic_logo: string | null;
  clinic_email: string | null;
  clinic_phone: string | null;
  patient_id: string;
  clinician_id: string | null;
  clinician_name: string | null;
  service_id: string | null;
  service_name: string | null;
  service_color: string | null;
  starts_at: string;
  ends_at: string;
  status: AppointmentStatus;
  display_status: PatientFacingAppointmentStatus;
  notes: string | null;
  payment_status: PaymentStatus | null;
  payment_timing: PaymentTiming;
  cancellation_allowed: boolean;
  rescheduling_allowed: boolean;
  cancel_until: string;
  payment_kind: PortalPaymentKind | null;
  video_meeting_url: string | null;
}

export const portalAppointmentsQueryKey = (patientId: string | null) =>
  ['portal', 'appointments', patientId] as const;

function portalUserDisplayName(
  name: string | null | undefined,
  email: string | null | undefined,
): string | null {
  return practitionerDisplayName(name?.trim() || email?.trim() || '', '') || null;
}

/**
 * Hook to fetch appointments for the active portal clinic.
 */
export function usePortalAppointments() {
  const { activePatientId, isLoading: clinicsLoading } = usePortalActiveClinic();

  const query = useQuery({
    queryKey: portalAppointmentsQueryKey(activePatientId),
    enabled: Boolean(activePatientId),
    queryFn: async (): Promise<PortalAppointmentItem[]> => {
      if (!activePatientId) return [];

      const APPOINTMENT_SELECT_WITH_TIMING =
        'id, clinic_id, patient_id, clinician_id, service_id, service_name, starts_at, ends_at, status, notes, ' +
        'payment_method, payment_status, visit_status, video_meeting_url, ' +
        'clinic:clinics!appointments_clinic_id_fkey(name,branding,slug,email,phone), ' +
        'clinician:users!appointments_clinician_id_fkey(name,email), ' +
        'service:services!appointments_service_id_fkey(color,payment_timing,require_prepayment)';
      const APPOINTMENT_SELECT_BASIC =
        'id, clinic_id, patient_id, clinician_id, service_id, service_name, starts_at, ends_at, status, notes, ' +
        'payment_method, payment_status, visit_status, video_meeting_url, ' +
        'clinic:clinics!appointments_clinic_id_fkey(name,branding,slug,email,phone), ' +
        'clinician:users!appointments_clinician_id_fkey(name,email), ' +
        'service:services!appointments_service_id_fkey(color)';

      const [appointmentsFirst, invoicesRes] = await Promise.all([
        supabase
          .from('appointments')
          .select(APPOINTMENT_SELECT_WITH_TIMING)
          .eq('patient_id', activePatientId)
          .order('starts_at', { ascending: false }),
        supabase
          .from('invoices')
          .select('appointment_id, payment_method, status')
          .eq('patient_id', activePatientId)
          .eq('status', 'paid')
          .not('appointment_id', 'is', null),
      ]);

      let appointmentsRes = appointmentsFirst;
      if (appointmentsRes.error && isPostgrestMissingColumnError(appointmentsRes.error)) {
        appointmentsRes = await supabase
          .from('appointments')
          .select(APPOINTMENT_SELECT_BASIC)
          .eq('patient_id', activePatientId)
          .order('starts_at', { ascending: false });
      }

      if (appointmentsRes.error) throw appointmentsRes.error;
      if (invoicesRes.error) throw invoicesRes.error;

      const invoiceMethodByAppointment = new Map<string, string | null>();
      for (const invoice of invoicesRes.data ?? []) {
        if (!invoice.appointment_id) continue;
        const current = invoiceMethodByAppointment.get(invoice.appointment_id);
        const next = invoice.payment_method;
        const currentIsPreferred = isPreferredInvoicePaymentMethod(current);
        const nextIsPreferred = isPreferredInvoicePaymentMethod(next);
        if (current === undefined || (nextIsPreferred && !currentIsPreferred)) {
          invoiceMethodByAppointment.set(invoice.appointment_id, next);
        }
      }

      type Joined = {
        id: string;
        clinic_id: string;
        patient_id: string;
        clinician_id: string | null;
        service_id: string | null;
        service_name: string | null;
        starts_at: string;
        ends_at: string;
        status: AppointmentStatus;
        notes: string | null;
        payment_method: string | null;
        payment_status: PaymentStatus | null;
        visit_status: VisitStatus | null;
        video_meeting_url: string | null;
        clinic: {
          name: string;
          branding: Json | null;
          slug: string | null;
          email: string | null;
          phone: string | null;
        } | null;
        clinician: { name: string | null; email: string } | null;
        service: {
          color: string | null;
          payment_timing?: string | null;
          require_prepayment?: boolean | null;
        } | null;
      };

      const joined = (appointmentsRes.data ?? []) as unknown as Joined[];
      const practitionerNameById = new Map<string, string>();
      const extraByAppointment = new Map<string, string[]>();
      const appointmentIds = joined.map((row) => row.id);
      if (appointmentIds.length > 0) {
        const { data: extraRows } = await supabase
          .from('appointment_practitioners')
          .select('appointment_id, practitioner_id')
          .in('appointment_id', appointmentIds);
        for (const row of extraRows ?? []) {
          const list = extraByAppointment.get(row.appointment_id) ?? [];
          list.push(row.practitioner_id);
          extraByAppointment.set(row.appointment_id, list);
        }
      }
      const practitionerIds = new Set<string>();
      for (const row of joined) {
        const ids = extraByAppointment.get(row.id) ?? [];
        if (row.clinician_id && !ids.includes(row.clinician_id)) ids.unshift(row.clinician_id);
        extraByAppointment.set(row.id, ids);
        for (const id of ids) practitionerIds.add(id);
      }
      if (practitionerIds.size > 0) {
        const { data: users } = await supabase
          .from('users')
          .select('id, name, email')
          .in('id', [...practitionerIds]);
        for (const user of users ?? []) {
          const label = portalUserDisplayName(user.name, user.email);
          if (label) practitionerNameById.set(user.id, label);
        }
      }
      const missingIds = [...practitionerIds].filter((id) => !practitionerNameById.has(id));
      if (missingIds.length > 0) {
        const slugs = [
          ...new Set(joined.map((row) => row.clinic?.slug).filter((slug): slug is string => Boolean(slug))),
        ];
        for (const slug of slugs) {
          const { data } = await supabase.functions.invoke<{
            ok: true;
            data: { clinicians?: Array<{ id: string; name: string }> };
          }>('public-list-bookable-services', {
            method: 'POST',
            body: { clinic_slug: slug },
          });
          for (const clinician of data?.data?.clinicians ?? []) {
            const label = portalUserDisplayName(clinician.name, null);
            if (label) practitionerNameById.set(clinician.id, label);
          }
        }
      }
      const clinicIds = [...new Set(joined.map((row) => row.clinic_id))];
      const policyByClinicId = new Map(
        clinicIds.map((clinicId) => [clinicId, resolvePatientPortalClinicPolicy(null)] as const),
      );
      const clinicPaymentTiming = new Map<string, unknown>();

      if (clinicIds.length > 0) {
        const policyRes = await supabase
          .from('clinics')
          .select(
            'id, cancellation_deadline_hours, allow_patient_rescheduling, patient_cancel_before_deadline, patient_cancel_after_deadline, payment_timing',
          )
          .in('id', clinicIds);

        if (policyRes.error) {
          if (!isPostgrestMissingColumnError(policyRes.error)) throw policyRes.error;
        } else {
          for (const row of policyRes.data ?? []) {
            policyByClinicId.set(row.id, resolvePatientPortalClinicPolicy(row));
            clinicPaymentTiming.set(row.id, (row as { payment_timing?: unknown }).payment_timing);
          }
        }
      }

      return joined.map(
        (r): PortalAppointmentItem => {
          const policy = policyByClinicId.get(r.clinic_id) ?? resolvePatientPortalClinicPolicy(null);
          const displayStatus = resolvePatientFacingAppointmentStatus({
            status: r.status,
            visitStatus: r.visit_status,
            startsAt: r.starts_at,
            endsAt: r.ends_at,
          });
          const paymentTiming = resolvePaymentTiming({
            clinicPaymentTiming: clinicPaymentTiming.get(r.clinic_id),
            servicePaymentTiming: r.service?.payment_timing,
            serviceRequirePrepayment: r.service?.require_prepayment,
          });
          const cancellationAllowed = isPatientCancellationAllowed({
            appointmentStatus: r.status,
            startsAt: r.starts_at,
            deadlineHours: policy.deadlineHours,
            beforeDeadline: policy.beforeDeadline,
            afterDeadline: policy.afterDeadline,
          });
          const reschedulingAllowed = isPatientReschedulingAllowed({
            appointmentStatus: r.status,
            startsAt: r.starts_at,
            allowPatientRescheduling: policy.allowPatientRescheduling,
            deadlineHours: policy.deadlineHours,
          });

          return {
            id: r.id,
            clinic_id: r.clinic_id,
            clinic_name: r.clinic?.name ?? '—',
            clinic_slug: r.clinic?.slug ?? null,
            clinic_logo: resolveClinicLogoUrl(logoUrlFromBranding(r.clinic?.branding)),
            clinic_email: r.clinic?.email ?? null,
            clinic_phone: r.clinic?.phone ?? null,
            patient_id: r.patient_id,
            clinician_id: r.clinician_id,
            clinician_name:
              portalUserDisplayName(r.clinician?.name, r.clinician?.email) ??
              (r.clinician_id ? practitionerNameById.get(r.clinician_id) : undefined) ??
              extraByAppointment
                .get(r.id)
                ?.map((id) => practitionerNameById.get(id))
                .filter((name): name is string => Boolean(name))
                .join(', ') ??
              null,
            service_id: r.service_id,
            service_name: r.service_name,
            service_color: r.service?.color ?? null,
            starts_at: r.starts_at,
            ends_at: r.ends_at,
            status: r.status,
            display_status: displayStatus,
            notes: r.notes,
            video_meeting_url: r.video_meeting_url ?? null,
            payment_status: r.payment_status,
            payment_timing: paymentTiming,
            cancellation_allowed: cancellationAllowed,
            rescheduling_allowed: reschedulingAllowed,
            cancel_until: new Date(
              new Date(r.starts_at).getTime() - policy.deadlineHours * 3_600_000,
            ).toISOString(),
            payment_kind: resolvePortalPaymentKind({
              payment_method: r.payment_method,
              payment_status: r.payment_status,
              visit_status: r.visit_status,
              invoice_payment_method: invoiceMethodByAppointment.get(r.id) ?? null,
              has_paid_invoice: invoiceMethodByAppointment.has(r.id),
            }),
          };
        }
      );
    },
    staleTime: 15_000,
  });

  return {
    ...query,
    isLoading: clinicsLoading || query.isLoading,
  };
}

export function usePortalRescheduleAppointment() {
  const qc = useQueryClient();
  const { activePatientId } = usePortalActiveClinic();

  return useMutation({
    mutationFn: async (input: {
      appointment_id: string;
      starts_at: string;
      ends_at: string;
      clinician_id?: string | null;
    }) => {
      const { data, error } = await supabase.rpc('portal_reschedule_appointment', {
        p_appointment_id: input.appointment_id,
        p_starts_at: input.starts_at,
        p_ends_at: input.ends_at,
        p_clinician_id: input.clinician_id ?? undefined,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: portalAppointmentsQueryKey(activePatientId),
      });
    },
  });
}

export function usePortalCancelAppointment() {
  const qc = useQueryClient();
  const { activePatientId } = usePortalActiveClinic();

  return useMutation({
    mutationFn: async (input: {
      appointment_id: string;
      reason?: string | null;
    }) => {
      const { data, error } = await supabase.rpc('portal_cancel_appointment', {
        p_appointment_id: input.appointment_id,
        p_reason: input.reason ?? 'Cancelled by patient via portal',
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: portalAppointmentsQueryKey(activePatientId),
      });
    },
  });
}
